import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { TPageNotification } from '../../../types';
import { TRootState } from '../../../reducers';
import { useTimesheetApproval } from './useTimesheetApproval';
import TimesheetApprovalContent from './TimesheetApprovalContent';
import './approval.style.scss';

type ClientTimesheetApprovalPageProps = {
    notification: TPageNotification;
};

const TimesheetApprovalPage: React.FC<ClientTimesheetApprovalPageProps> = ({ notification }) => {
    const clientData = useSelector((state: TRootState) => state.clientData?.basicInfo);
    const referralCode = clientData?.referral_code?.trim();
    const hasValidReferralCode = Boolean(referralCode) && !referralCode?.toLowerCase().startsWith('ref');
    const isEligible =
        clientData?.electronic_authorisation === true &&
        clientData?.on_stop === false &&
        hasValidReferralCode;

    const {
        pageState,
        timesheets,
        errorType,
        errorMessage,
        loadTimesheets,
        handleApprove,
        handleReject,
    } = useTimesheetApproval({
        // No token - uses session auth
        onApproveSuccess: count => {
            const message =
                count === 1
                    ? 'Timesheet approved successfully'
                    : `${count} timesheets approved successfully`;
            notification.success('Success', message);
        },
        onApproveError: message => {
            notification.error('Error', message);
        },
        onRejectSuccess: () => {
            notification.success('Success', 'Timesheet rejected successfully');
        },
        onRejectError: message => {
            notification.error('Error', message);
        },
    });

    // Load timesheets when the client becomes eligible
    useEffect(() => {
        if (isEligible) {
            loadTimesheets();
        }
    }, [isEligible, loadTimesheets]);

    if (!clientData) {
        return (
            <div className="client-timesheet-approval-page">
                <TimesheetApprovalContent
                    pageState="loading"
                    timesheets={[]}
                    errorType="general"
                    errorMessage=""
                    showErrorPage={false}
                />
            </div>
        );
    }

    if (!isEligible) {
        return (
            <div className="client-timesheet-approval-page">
                <div className="client-timesheet-error">
                    <div className="client-timesheet-error__icon">
                        <i className="icon icon-warning" />
                    </div>
                    <h2 className="client-timesheet-error__title">Timesheet Approval Unavailable</h2>
                </div>
            </div>
        );
    }

    return (
        <div className="client-timesheet-approval-page">
            <TimesheetApprovalContent
                pageState={pageState}
                timesheets={timesheets}
                errorType={errorType}
                errorMessage={errorMessage}
                onApprove={handleApprove}
                onReject={handleReject}
                onRetry={loadTimesheets}
                showErrorPage={false}
                skipApprovalModal={true}
            />
        </div>
    );
};

export default TimesheetApprovalPage;
